import { BaseService } from "./common/base";

/** What became of the shadow checks of one repository method. */
export type ShadowMethodReport = {
  method: string;
  kind: "read" | "write";
  same?: number;
  differs?: number;
  "too-large"?: number;
  lagging?: number;
  timeout?: number;
  failed?: number;
  skipped?: number;
  "dgraph-failed"?: number;
  "copy-failed"?: number;
  /** same / (same + differs), null when nothing was compared */
  agreement: number | null;
};

export type ShadowReport = {
  backend: "dgraph" | "shadow" | "postgres";
  /** when the reporting API instance started: counts are since then */
  since: string;
  methods: ShadowMethodReport[];
};

export type AdminUserType =
  | "patient"
  | "doctor"
  | "assistant"
  | "organization"
  | "organizationManager"
  | "midwife"
  | "dispatchManager"
  | "dispatchOperator"
  | "admin";

/** One line of `GET /admin/users`. */
export type AdminUser = {
  id: string;
  username: string | null;
  userType: AdminUserType | null;
  creation: string | null;
  /** Profile name(s), joined when the user has several profiles. */
  name: string | null;
};

export type AdminUserList = { total: number; users: AdminUser[] };

export type AccountDeletionStatus =
  | "PENDING"
  | "PURGING"
  | "COMPLETED"
  | "FAILED"
  | "CANCELLED";

export type AccountDeletionStep = {
  step: string;
  outcome: "DONE" | "SKIPPED" | "PARTIAL" | "FAILED";
  detail?: string;
  at: string;
};

/** A pending or failed erasure, from `GET /admin/account-deletions`. */
export type AccountDeletion = {
  id: string;
  userRef?: string;
  userType?: string;
  reason?: "SELF_SERVICE" | "ADMIN";
  status: AccountDeletionStatus;
  requestedAt: string;
  scheduledFor: string;
  completedAt?: string;
  attempts?: number;
  /** JSON array of AccountDeletionStep */
  steps?: string;
};

export type AccountDeletionResult = {
  id: string;
  status: AccountDeletionStatus | "PARTIAL";
  steps?: AccountDeletionStep[];
};

export type ApiHealth = {
  uptime: number;
  time: number;
  status: string;
  subServices: Record<string, "ok" | "error">;
};

/** What support sees of a user, from `GET /admin/users/:id` (no health data). */
export type AdminUserFile = {
  id: string;
  username: string | null;
  userType: AdminUserType | null;
  creation: string | null;
  account: {
    timeJoined: number;
    loginMethods: Array<{
      method: string;
      email?: string;
      phoneNumber?: string;
      verified: boolean;
      timeJoined: number;
    }>;
  } | null;
  profile: {
    id: string | null;
    active: boolean | null;
    name: string | null;
    rpps: string | null;
    siret: string | null;
    telecom: Array<{ system: string | null; value: string | null }>;
    organizations: Array<{ id: string; name: string | null }>;
  } | null;
  appointments: Array<{
    id: string;
    start: string | null;
    end: string | null;
    status: string | null;
    serviceType: string | null;
  }>;
  deletion: {
    status: AccountDeletionStatus;
    requestedAt: string;
    scheduledFor: string;
  } | null;
};

/** One admin action, from `GET /admin/audit`. */
export type AdminAuditEvent = {
  id: string;
  at: string;
  actorRef: string;
  actorLabel?: string;
  action: string;
  targetType?: string;
  targetRef?: string;
  reason?: string;
  /** JSON without health data */
  details?: string;
};

export type AdminAuditPage = { total: number; events: AdminAuditEvent[] };

export class AdminService extends BaseService {
  constructor() {
    super("admin");
  }

  getAppointmentStatistics(params: {
    periodStartDate: Date;
    periodEndDate: Date;
  }) {
    return this._get("/appointments/statistics", {
      params,
    });
  }

  findAppointments(params?: { first?: number; offset?: number }) {
    return this._get("/appointments", {
      params,
    });
  }

  getShadowReport(): Promise<ShadowReport> {
    return this._get("/shadow-report");
  }

  findUsers(params: {
    search?: string;
    userType?: AdminUserType;
    first?: number;
    offset?: number;
  }): Promise<AdminUserList> {
    return this._get("/users", { params });
  }

  /** Immediate and irreversible erasure of the account; the reason is audited. */
  deleteUser(id: string, reason?: string): Promise<AccountDeletionResult> {
    return this._delete(`/users/${id}`, {
      params: reason ? { reason } : undefined,
    });
  }

  /** The user's file; each opening is written to the audit trail. */
  getUserFile(id: string, reason?: string): Promise<AdminUserFile> {
    return this._get(`/users/${id}`, {
      params: reason ? { reason } : undefined,
    });
  }

  findAudit(params: {
    actorRef?: string;
    targetRef?: string;
    action?: string;
    first?: number;
    offset?: number;
  }): Promise<AdminAuditPage> {
    return this._get("/audit", { params });
  }

  findAccountDeletions(): Promise<AccountDeletion[]> {
    return this._get("/account-deletions");
  }

  getHealth(): Promise<ApiHealth> {
    return this._get("/health");
  }

  findSupplyRequests() {
    return this._get("supply-requests");
  }

  getSupplyRequests(id: string) {
    return this._get(`supply-requests/${id}`);
  }
}
